
import { useState, useCallback, useEffect, useRef } from 'react';
import { CampingSpot, MapBounds } from '../types';
import { detectCountry } from '../utils/countryDetection';
import {
  upsertSpotsToFirebase,
  getCachedSpotsInArea,
  fetchSlimSpotMarkers,
  deleteAlgorithmicSpotsInBounds,
  fetchSpotsByIds,
  scanAreaServer,
  db
} from '../services/firebaseService';
import { doc, onSnapshot } from 'firebase/firestore';
import { fetchCandidateSpots } from '../services/overpassService';
import { addVerificationMarkerToSpot, enrichSpotWithGeoData, isAiUnconfirmedSpot, stripVerificationMarkerFromSpot } from '../services/geoValidationService';
import { getExternalIdentityKey, mergeSpotsByIdentity } from '../services/spotIdentity';
import {
  searchCircleForBounds, boundsForCircle, needsNewSearch, type SearchCircle,
} from '../utils/searchArea';
import { getCachedSpotsWithMeta, classifyFreshness, saveSpotsToIDB, removeSpotFromIDB } from '../services/spotCacheService';
import { recordView, isBoundsHotSync } from '../services/regionUsageService';

export type ScanProgress = {
  phase: 'overpass' | 'processing' | 'enriching' | 'saving' | 'done';
  current: number;
  total: number;
} | null;

/**
 * Nedre grense for å HENTE plasser. Var 8, som gjorde zoom til et premiss: sto du
 * lenger ute, gjorde «Søk i dette området» ingenting.
 *
 * Grensen fantes fordi et søk pleide å hente utsnittets bbox, som vokste fritt med
 * zoom. Søkeområdet er nå en sirkel med hardt tak (MAX_SEARCH_RADIUS_KM = 50), så
 * ett søk koster det samme uansett zoom. Det som står igjen er bare en vakt mot
 * meningsløse verdensomspennende utsnitt.
 */
const MIN_ZOOM_FOR_SPOT_FETCH = 5;

/**
 * `isDrivingRef` skrur AV «Søk i dette området»-terskelen.
 *
 * Under navigasjon og auto-nav følger kartet kjøretøyet, og det er uakseptabelt å
 * kreve et knappetrykk i 80 km/t. Da hentes plasser automatisk som før. Terskelen
 * gjelder altså bare utforskingsmodus, der brukeren panorerer bevisst.
 *
 * En REF og ikke en verdi, fordi kjøretilstanden (`followRouteState`, `autoNav`)
 * defineres langt NEDENFOR dette hook-kallet i App.tsx. Refen leses i det øyeblikket
 * utsnittet endrer seg, som er nøyaktig når svaret trengs — så vi slipper å flytte
 * rundt på rekkefølgen i en 7000-linjers komponent.
 */
export const useSpotManager = (currentViewBounds: MapBounds | null, favoriteSpotIds: string[] = [], mapZoom: number, uid?: string | null, enabledCountries?: Partial<Record<string, boolean>>, isDrivingRef?: { current: boolean }, userDraggedRef?: { current: boolean }) => {
  const [spots, setSpots] = useState<CampingSpot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBounds, setIsFetchingBounds] = useState(false);
  const [spotsReady, setSpotsReady] = useState(false);
  const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(false);
  const [scanProgress, setScanProgress] = useState<ScanProgress>(null);
  const activeScanRunRef = useRef(0);
  const fetchedFavoriteIdsRef = useRef<Set<string>>(new Set());
  const progressUnsubRef = useRef<(() => void) | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoad = useRef(true);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);
  // Sirkelen vi sist søkte i. Er hele utsnittet innenfor den, har vi dataene og
  // trenger ikke gjøre noe. Stikker et hjørne utenfor, tilbys «Søk her».
  const lastSearchCircleRef = useRef<SearchCircle | null>(null);
  const [searchCircle, setSearchCircle] = useState<SearchCircle | null>(null);
  const [needsManualSearch, setNeedsManualSearch] = useState(false);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);
  const [initHidden] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('fricamp_hidden_spots');
      return stored ? new Set<string>(JSON.parse(stored) as string[]) : new Set<string>();
    } catch { return new Set<string>(); }
  });
  const bannedSpotIdsRef = useRef<Set<string>>(initHidden);

  /**
   * LAND-GATINGEN LIGGER IKKE HER LENGER — den kjører ved TEGNING, i
   * `visibleLayerSpots` (components/Map.tsx).
   *
   * Hvorfor flyttet: dette filteret kjørte når plassene ble tatt inn i minnet.
   * Ved sidelast er `currentUser` fortsatt null i noen hundre millisekunder mens
   * Firebase Auth svarer, så `effectiveNordicConfig` er den anonyme
   * `{sweden:true, denmark:false}`. Første henting rakk å bli filtrert med DEN,
   * og de danske plassene var borte for godt — ingen ny henting skjer, for
   * søkesirkelen er jo allerede dekket. Målt i produksjon 15.08.2026: admin var
   * innlogget (`jonborgentakle@gmail.com`, isAdmin i basen), og kartet var
   * likevel tomt over Jylland.
   *
   * Ved tegning er filteret reaktivt: idet rollen løses, dukker plassene opp
   * uten at noe må hentes på nytt.
   */
  const filterSafeSpots = useCallback((spots: CampingSpot[]): CampingSpot[] => {
    const beholdt = spots.filter((s) => {
      if (bannedSpotIdsRef.current.has(s.id)) return false;
      if (s.isDeleted) return false;
      // Camping forbudt-markeringer lagres med isInvalid: true, men vi vil
      // beholde dem her slik at App.tsx kan vise dem som advarsel ved zoom ≥ 16
      // (kun hvis verifisert med bilde). Andre isInvalid-spots filtreres bort.
      if (s.isInvalid && s.spotType !== 'forbidden_area') return false;
      if (isAiUnconfirmedSpot(s) && s.isInnmark === true) return false;
      if (s.isInnmark === true && (s.score ?? 10) <= 3 && s.sourceLevel !== 4 && s.spotType !== 'confirmed' && s.spotType !== 'commercial_campsite' && !s.isVerified) return false;
      return true;
    });
    return beholdt;
  }, []);

  // Define loading logic separately to be reusable
  const fetchSpotsForBounds = async (bounds: MapBounds) => {
      // Avoid heavy marker fetches while the map is still zoomed far out.
      if (mapZoom < MIN_ZOOM_FOR_SPOT_FETCH && !isFirstLoad.current) return;

      // Cancel previous in-flight fetch to prevent stale data overwrites
      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      const myFetchId = ++fetchIdRef.current;

      // Only show global spinner on first load
      if (isFirstLoad.current) setIsLoading(true);
      setIsFetchingBounds(true);

      try {
        // Registrer at brukeren ser på området (frekvens-signal → hot-cell-caching).
        recordView(bounds);

        // Step 0: Show cached data instantly (stale-while-revalidate)
        const { spots: cachedSpots, oldestSavedAt } = await getCachedSpotsWithMeta(bounds);
        const freshness = classifyFreshness(oldestSavedAt, isBoundsHotSync(bounds));
        if (cachedSpots.length > 0) {
          const safeIdb = filterSafeSpots(cachedSpots);
          if (safeIdb.length > 0) {
            setSpots(prev => mergeSpotsByIdentity(prev, safeIdb));
            setSpotsReady(true);
            // If first load, hide spinner early since we have data to show
            if (isFirstLoad.current) {
              setIsLoading(false);
            }
          }
        } else if (isFirstLoad.current) {
          // No local cache — show first-time loading hint
          setIsFirstTimeLoad(true);
        }

        // Fersk nok cache? Hopp over nett-revalidering helt — dette er det som
        // fjerner «masse lasting» i områder du nettopp (eller ofte) har sett.
        // Hot celler har et mye lengre fersk-vindu (se classifyFreshness).
        if (freshness === 'fresh' && cachedSpots.length > 0) {
          return; // finally-blokken rydder opp (spotsReady, spinner, isFirstLoad)
        }

        // Step 1: Slim Cloud Function fetch (only marker fields, ~10x less data)
        let slimDelivered = false;
        if (fetchIdRef.current !== myFetchId) return; // stale fetch
        try {
          const slimSpots = await fetchSlimSpotMarkers(bounds, mapZoom);
          if (fetchIdRef.current !== myFetchId) return; // stale fetch
          if (slimSpots.length > 0) {
            const safeSlim = filterSafeSpots(slimSpots);
            setSpots(prev => mergeSpotsByIdentity(prev, safeSlim));
            setSpotsReady(true);
            slimDelivered = true;
            if (isFirstLoad.current) setIsLoading(false);
            // Save slim data to IDB for instant repeat visits
            saveSpotsToIDB(safeSlim).catch(() => {});
          }
        } catch (slimErr) {
          console.warn('[useSpotManager] Slim endpoint failed, falling back to Firestore:', slimErr);
        }

        // Step 2: Full Firestore query (enriches with all fields, or serves as fallback)
        if (!slimDelivered && fetchIdRef.current === myFetchId) {
          // Slim failed — use Firestore with tier callback as fallback
          const tierCallback = (tier1Spots: CampingSpot[]) => {
            const safeTier1 = filterSafeSpots(tier1Spots);
            if (safeTier1.length > 0) {
              setSpots(prev => mergeSpotsByIdentity(prev, safeTier1));
            }
          };
          const allSpots = await getCachedSpotsInArea(bounds, { zoom: mapZoom, tierCallback });
          if (allSpots.length > 0) {
            const safeCached = filterSafeSpots(allSpots);
            setSpots(prev => mergeSpotsByIdentity(prev, safeCached));
            setSpotsReady(true);
            saveSpotsToIDB(safeCached).catch(() => {});
          }
        }
      } catch (e) {
        console.warn("Failed to load cached spots", e);
      } finally {
        setSpotsReady(true); // Mark ready even on error/empty so other layers aren't blocked forever
        setIsFetchingBounds(false);
        setIsFirstTimeLoad(false);
        if (isFirstLoad.current) {
            setIsLoading(false);
            isFirstLoad.current = false;
        }
      }
  };

  const prevEnabledCountriesRef = useRef(enabledCountries);
  useEffect(() => {
    const prev = prevEnabledCountriesRef.current;
    prevEnabledCountriesRef.current = enabledCountries;
    if (!currentViewBounds) return;
    const keys = ['sweden', 'denmark', 'finland', 'iceland'];
    const newlyEnabled = keys.some(k => !prev?.[k] && enabledCountries?.[k]);
    if (newlyEnabled) {
      // Nytt land slått på = nye plasser i samme område. Hent automatisk, og
      // NULLSTILL sirkelen: området er «søkt», men uten det nye landet.
      lastSearchCircleRef.current = null;
      runSearch(currentViewBounds);
    }
  }, [enabledCountries]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Henter innenfor en søkesirkel med hardt tak på radien.
   *
   * Hvorfor sirkel og ikke bare utsnittet: en viewport-bbox har ingen øvre grense.
   * Zoomer brukeren ut, vokser området fritt — det var nettopp derfor lav zoom var
   * dyrt. Taket (MAX_SEARCH_RADIUS_KM) gjør kostnaden per søk forutsigbar uansett
   * zoom. Sirkelen tegnes på kartet, så brukeren ser hvilket område som ble søkt.
   */
  const runSearch = useCallback((bounds: MapBounds) => {
    const sirkel = searchCircleForBounds(bounds);
    // Spør på bbox-en som omslutter sirkelen. Vi filtrerer IKKE bort hjørnene:
    // de er alt betalt for, og å skjule dem ville også fjernet markører fra
    // tidligere søk. Sirkelen viser hva som ble søkt, ikke hva som vises.
    fetchSpotsForBounds(boundsForCircle(sirkel));
    lastFetchedBoundsRef.current = bounds;
    lastSearchCircleRef.current = sirkel;
    setSearchCircle(sirkel);
    setNeedsManualSearch(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** «Søk i dette området» — brukerens bevisste trykk. */
  const searchHere = useCallback(() => {
    if (!currentViewBounds || mapZoom < MIN_ZOOM_FOR_SPOT_FETCH) return;
    runSearch(currentViewBounds);
  }, [currentViewBounds, mapZoom, runSearch]);

  useEffect(() => {
    if (!currentViewBounds || mapZoom < MIN_ZOOM_FOR_SPOT_FETCH) return;

    // Clear existing timeout
    if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
    }

    const opprydding = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      fetchAbortRef.current?.abort();
    };
    const hentSnart = () => {
      timeoutRef.current = setTimeout(() => runSearch(currentViewBounds), 300);
      return opprydding;
    };

    // Første visning henter alltid — et tomt kart med en søkeknapp leses som at
    // appen er ødelagt.
    if (isFirstLoad.current) {
      runSearch(currentViewBounds);
      return opprydding;
    }

    // Har brukeren verken flyttet seg nok eller zoomet nok ut siden forrige søk,
    // har vi dataene — ingen henting, ingen knapp. Dette er også det som hindrer
    // «blafring»: småjusteringer holder seg innenfor slakken i søkesirkelen.
    if (!needsNewSearch(currentViewBounds, lastSearchCircleRef.current)) {
      setNeedsManualSearch(false);
      return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    }

    // Under kjøring hentes alt automatisk — man kan ikke kreve knappetrykk i 80 km/t.
    if (isDrivingRef?.current === true) return hentSnart();

    // Flyttet APPEN kartet (søketreff, «vis i kart», valgt plass)? Da har brukeren
    // alt uttrykt hvor hen vil se, og et ekstra trykk er bare i veien. Map.tsx
    // markerer sine egne flytt — se utils/mapMoveOrigin.
    if (userDraggedRef?.current !== true) return hentSnart();

    // HER LÅ EN ZOOM-PORT, OG DEN GJORDE KARTET STUMT.
    //
    // Den returnerte uten å gjøre noe hvis `mapZoom` hadde endret seg siden forrige
    // gang. Men vi er bare kommet hit fordi `needsNewSearch` alt har slått fast at
    // utsnittet ligger UTENFOR det vi har hentet. Å returnere der betyr: ingen
    // henting, og ingen knapp — brukeren ser et tomt kart uten noen måte å be om
    // data på.
    //
    // På telefon var det normaltilfellet, ikke et kanttilfelle: knipe-zoom flytter
    // og zoomer i samme bevegelse, så nesten hver eneste navigering traff porten.
    // På desktop er panorering (musedrag) og zoom (hjul) atskilte bevegelser, og
    // derfor så det riktig ut da det ble verifisert i nettleser.
    //
    // Zoom skal ikke utløse HENTING — det gjør den ikke: linjen over sender bare
    // appens egne flytt til `hentSnart()`. Brukerens zoom lander her, på knappen.
    setNeedsManualSearch(true);

    return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        fetchAbortRef.current?.abort();
    };
  }, [currentViewBounds, mapZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!favoriteSpotIds || favoriteSpotIds.length === 0) return;
    // Use ref to track already-fetched IDs instead of depending on spots state
    const missingFavoriteIds = favoriteSpotIds.filter((id) => !fetchedFavoriteIdsRef.current.has(id));
    if (missingFavoriteIds.length === 0) return;

    // Mark as fetched immediately to prevent duplicate requests
    missingFavoriteIds.forEach((id) => fetchedFavoriteIdsRef.current.add(id));

    let isCancelled = false;
    fetchSpotsByIds(missingFavoriteIds)
      .then((favoriteSpots) => {
        if (isCancelled || favoriteSpots.length === 0) return;
        setSpots((prev) => {
          return mergeSpotsByIdentity(prev, favoriteSpots);
        });
      })
      .catch((e) => {
        // Remove from fetched set so they can be retried
        missingFavoriteIds.forEach((id) => fetchedFavoriteIdsRef.current.delete(id));
        console.warn("Failed to load missing favorite spots", e);
      });

    return () => {
      isCancelled = true;
    };
  }, [favoriteSpotIds]);

  // --- Client-side fallback scan (original logic) ---
  const scanAreaClientFallback = useCallback(async (signal?: AbortSignal) => {
    if (!currentViewBounds) return null;

    const latSpan = currentViewBounds.latMax - currentViewBounds.latMin;
    const lonSpan = currentViewBounds.lonMax - currentViewBounds.lonMin;
    const midLat = (currentViewBounds.latMin + currentViewBounds.latMax) / 2;
    const areaKm2 = latSpan * 111 * lonSpan * 111 * Math.cos((midLat * Math.PI) / 180);
    if (areaKm2 > 3000) {
      console.warn(`scanArea skipped: viewport too large (${Math.round(areaKm2)} km²). Zoom in to scan.`);
      return null;
    }

    setIsLoading(true);
    try {
      const runId = Date.now();
      activeScanRunRef.current = runId;

      const freshValidSpots = await fetchCandidateSpots(
        currentViewBounds,
        { minDistanceHouses: 150, nearWater: false },
        [],
        signal
      );

      if (signal?.aborted || activeScanRunRef.current !== runId) return null;

      const quickSpots = freshValidSpots.map((spot) =>
        isAiUnconfirmedSpot(spot) ? addVerificationMarkerToSpot(spot) : spot
      );
      const validIds = new Set(quickSpots.map(s => s.id));
      const validExternalKeys = new Set(
        quickSpots.map((s) => getExternalIdentityKey(s)).filter((x): x is string => Boolean(x))
      );

      setSpots(prev => {
        const keptSpots = prev.filter(spot => {
          const inBounds =
            spot.location.latitude >= currentViewBounds.latMin &&
            spot.location.latitude <= currentViewBounds.latMax &&
            spot.location.longitude >= currentViewBounds.lonMin &&
            spot.location.longitude <= currentViewBounds.lonMax;
          if (!inBounds) return true;
          const isManualSpot = spot.id.startsWith('manual-') || (spot.features || []).includes('MANUELT');
          if (isManualSpot) return true;
          if (favoriteSpotIds.includes(spot.id) || spot.sourceLevel === 4) return true;
          if (spot.spotType !== 'algorithmic') return true;
          const extKey = getExternalIdentityKey(spot);
          if (!validIds.has(spot.id) && (!extKey || !validExternalKeys.has(extKey))) return false;
          return true;
        });
        const safeQuickSpots = filterSafeSpots(quickSpots);
        return mergeSpotsByIdentity(keptSpots, safeQuickSpots);
      });

      if (quickSpots.length > 0) await upsertSpotsToFirebase(quickSpots);
      setIsLoading(false);

      // Background enrichment
      const enrichConcurrently = async (spotsToEnrich: CampingSpot[], concurrency = 5): Promise<CampingSpot[]> => {
        const out: CampingSpot[] = [];
        let cursor = 0;
        const worker = async () => {
          while (cursor < spotsToEnrich.length) {
            const i = cursor++;
            const spot = spotsToEnrich[i];
            if (!spot) continue;
            try {
              const enriched = await enrichSpotWithGeoData(spot);
              out[i] = stripVerificationMarkerFromSpot(enriched);
            } catch {
              out[i] = stripVerificationMarkerFromSpot(spot);
            }
          }
        };
        await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, spotsToEnrich.length || 1)) }, () => worker()));
        return out.filter(Boolean);
      };

      void (async () => {
        if (quickSpots.length === 0) return;
        const maxTerrainSlopePercent = 9;
        const enriched = await enrichConcurrently(quickSpots, 5);
        if (activeScanRunRef.current !== runId) return;

        const removeIds = enriched
          .filter((s) => {
            if (s.sourceLevel === 4 || s.spotType === 'confirmed' || s.isVerified === true) return false;
            if (s.spotType === 'commercial_campsite') return false;
            if (isAiUnconfirmedSpot(s)) {
              if (s.isInnmark === true) return true;
              if (typeof s.terrainSlopePercent === 'number' && s.terrainSlopePercent > maxTerrainSlopePercent) return true;
            }
            if (s.isInnmark === true && (s.score ?? 10) <= 3) return true;
            return false;
          })
          .map((s) => s.id);

        const finalSpots = enriched.filter((s) => !removeIds.includes(s.id));
        const removedSpots = enriched
          .filter((s) => removeIds.includes(s.id))
          .map((s) => ({ ...s, isDeleted: true }));

        setSpots((prev) => {
          const next = prev.filter((s) => !removeIds.includes(s.id));
          const safeFinalSpots = filterSafeSpots(finalSpots);
          return mergeSpotsByIdentity(next, safeFinalSpots);
        });

        if (finalSpots.length > 0 || removedSpots.length > 0) {
          await upsertSpotsToFirebase([...finalSpots, ...removedSpots]);
        }
      })().catch((err) => {
        // Fire-and-forget background enrichment: swallow rejected persistence
        // writes (e.g. permission-denied for non-admins) so they never surface
        // as unhandled rejections.
        console.warn('[useSpotManager] background enrichment/persist failed:', err);
      });

      return quickSpots;
    } catch (err: any) {
      if (err.name === 'AbortError') return null;
      console.error("Scan error (client fallback):", err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [currentViewBounds, favoriteSpotIds]);

  // --- Main scanArea: tries server-side first, falls back to client ---
  const scanArea = useCallback(async (signal?: AbortSignal) => {
    if (!currentViewBounds) return null;

    const latSpan = currentViewBounds.latMax - currentViewBounds.latMin;
    const lonSpan = currentViewBounds.lonMax - currentViewBounds.lonMin;
    const midLat = (currentViewBounds.latMin + currentViewBounds.latMax) / 2;
    const areaKm2 = latSpan * 111 * lonSpan * 111 * Math.cos((midLat * Math.PI) / 180);
    if (areaKm2 > 3000) {
      console.warn(`scanArea skipped: viewport too large (${Math.round(areaKm2)} km²). Zoom in to scan.`);
      return null;
    }

    setIsLoading(true);
    setScanProgress(null);

    console.log('[scanArea] Starting scan, uid:', uid, 'bounds:', JSON.stringify(currentViewBounds));

    // Start listening to progress doc
    if (uid) {
      progressUnsubRef.current?.();
      const progressDoc = doc(db, 'system', `scan_progress_${uid}`);
      progressUnsubRef.current = onSnapshot(progressDoc, (snap) => {
        const d = snap.data();
        if (d?.phase) {
          setScanProgress({ phase: d.phase, current: d.current ?? 0, total: d.total ?? 0 });
        }
      }, (err) => { console.warn('[scanArea] Progress listener error:', err.message); });
    }

    try {
      const runId = Date.now();
      activeScanRunRef.current = runId;

      // Try server-side scan (all heavy lifting on Cloud Function)
      const serverResult = await scanAreaServer(currentViewBounds, true);
      console.log('[scanArea] Server returned:', serverResult.stats);

      if (signal?.aborted || activeScanRunRef.current !== runId) return null;

      const serverSpots = (serverResult.spots || []) as CampingSpot[];
      if (serverSpots.length === 0) {
        console.log('[scanArea] Server returned 0 spots');
        setIsLoading(false);
        return [];
      }

      const validIds = new Set(serverSpots.map(s => s.id));
      const validExternalKeys = new Set(
        serverSpots.map((s) => getExternalIdentityKey(s)).filter((x): x is string => Boolean(x))
      );

      setSpots(prev => {
        const keptSpots = prev.filter(spot => {
          const inBounds =
            spot.location.latitude >= currentViewBounds.latMin &&
            spot.location.latitude <= currentViewBounds.latMax &&
            spot.location.longitude >= currentViewBounds.lonMin &&
            spot.location.longitude <= currentViewBounds.lonMax;
          if (!inBounds) return true;
          const isManualSpot = spot.id.startsWith('manual-') || (spot.features || []).includes('MANUELT');
          if (isManualSpot) return true;
          if (favoriteSpotIds.includes(spot.id) || spot.sourceLevel === 4) return true;
          if (spot.spotType !== 'algorithmic') return true;
          const extKey = getExternalIdentityKey(spot);
          if (!validIds.has(spot.id) && (!extKey || !validExternalKeys.has(extKey))) return false;
          return true;
        });
        const safeServerSpots = filterSafeSpots(serverSpots);
        return mergeSpotsByIdentity(keptSpots, safeServerSpots);
      });

      console.log(`[scanArea] Server scan complete: ${serverResult.stats.kept} spots kept (${serverResult.stats.filtered} filtered)`);
      setIsLoading(false);
      return serverSpots;

    } catch (serverErr: any) {
      if (signal?.aborted) return null;
      console.warn('[scanArea] Server scan failed:', serverErr.code, serverErr.message, '→ falling back to client-side');
      setScanProgress(null);
      // Fallback to client-side processing
      const fallbackResult = await scanAreaClientFallback(signal);
      console.log('[scanArea] Client fallback returned:', fallbackResult?.length ?? 0, 'spots');
      const safeFallback = fallbackResult ? filterSafeSpots(fallbackResult) : null;
      return safeFallback;
    } finally {
      progressUnsubRef.current?.();
      progressUnsubRef.current = null;
      setScanProgress(null);
      setIsLoading(false);
    }
  }, [currentViewBounds, favoriteSpotIds, scanAreaClientFallback, uid]);

  const clearAlgorithmicSpots = useCallback(async () => {
    if (!currentViewBounds) return;
    await deleteAlgorithmicSpotsInBounds(currentViewBounds);
    setSpots(prev => prev.filter(spot => {
        if (spot.spotType !== 'algorithmic') return true;
        const inBounds = 
            spot.location.latitude >= currentViewBounds.latMin &&
            spot.location.latitude <= currentViewBounds.latMax &&
            spot.location.longitude >= currentViewBounds.lonMin &&
            spot.location.longitude <= currentViewBounds.lonMax;
        if (!inBounds) return true;
        return false;
    }));
  }, [currentViewBounds]);

  const updateSpotLocally = (updatedSpot: CampingSpot) => {
    setSpots(prev => prev.map(s => s.id === updatedSpot.id ? updatedSpot : s));
  };

  const removeSpotLocally = (id: string) => {
    console.log('[ADMIN-DELETE] removeSpotLocally called for', id);
    bannedSpotIdsRef.current.add(id);
    try { localStorage.setItem('fricamp_hidden_spots', JSON.stringify([...bannedSpotIdsRef.current])); } catch {}
    removeSpotFromIDB(id).catch(() => {});
    setSpots(prev => {
      const before = prev.length;
      const after = prev.filter(s => s.id !== id);
      console.log('[ADMIN-DELETE] spots array: before=%d after=%d (removed=%d)', before, after.length, before - after.length);
      return after;
    });
  };

  return {
    spots,
    setSpots,
    isLoading,
    setIsLoading,
    isFetchingBounds,
    spotsReady,
    isFirstTimeLoad,
    scanProgress,
    scanArea,
    updateSpotLocally,
    removeSpotLocally,
    clearAlgorithmicSpots,
    needsManualSearch,
    searchHere,
    searchCircle
  };
};
